import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { API } from 'constants';
import client from '../../../services/template-api';

import { useViewType } from '../../../hooks/useViewType';
import { useFetchDataOnLoad } from '../../../hooks/useFetchDataOnLoad';
import { useHandlePageDataLoadError } from '../../../hooks/useHandlePageDataLoadError';
import useDocumentTitle from '../../../hooks/useDocumentTitle';

import { generateUUID } from '../../../helpers/generateUUID';

import { VIEW_TYPES } from '../../../constants/viewTypes';

import Loading from '../../../components/Loading';
import PlaybookTemplateHeader from './PlaybookTemplateHeader/PlaybookTemplateHeader';
import PlaybookTemplateMainContent from './PlaybookTemplateMainContent/PlaybookTemplateMainContent';

const PlaybookTemplateDetailPage = () => {
    const { playbookTemplateId } = useParams();

    const navigate = useNavigate();
    const location = useLocation();

    const { viewType } = useViewType();

    const defaultBackLink = {
        [VIEW_TYPES.admin]: '/templates/playbooks',
        [VIEW_TYPES.client]: '/library/playbook',
    }[viewType];

    const prevLocation = location.state?.from;
    const prevLocationIsLibrary =
        prevLocation?.pathname?.includes('/library/playbook') ||
        prevLocation?.pathname?.includes('/templates/playbooks');

    const backLinkHref = prevLocationIsLibrary ? prevLocation : defaultBackLink;
    const { handlePageDataLoadError } = useHandlePageDataLoadError();

    const isEditable = !!location.pathname.startsWith('/templates/playbook/');

    const { data: playbookTemplate, setData: setPlaybookTemplate } = useFetchDataOnLoad({
        client,
        route: API.ROUTES.template.playbookTemplate + playbookTemplateId + '/',
        formatResponse: prepareTemplateDetails,
        onError: (e) =>
            handlePageDataLoadError({
                e,
                redirectPath: backLinkHref,
                generalErrorHandler: () => navigate(backLinkHref),
            }),
    });

    useDocumentTitle(playbookTemplate?.name);

    function prepareTemplateDetails(response) {
        const steps = response.default_version?.steps;
        const formattedSteps = (steps || [])
            .map((step) => ({
                ...step,
                temporaryId: generateUUID(),
            }))
            .sort((a, b) => a.order - b.order);

        return {
            ...response,
            default_version: { ...response.default_version, steps: formattedSteps },
        };
    }

    if (!playbookTemplate) {
        return (
            <div className="page-position bg-neutral-50 flex itc justify-center">
                <Loading />
            </div>
        );
    }

    const { id, name, description, icon_color, is_live } = playbookTemplate;

    return (
        <div className="page-position flex flex-col overflow-y-auto">
            <PlaybookTemplateHeader
                id={id}
                name={name}
                description={description}
                icon_color={icon_color}
                is_live={is_live}
                backLinkHref={backLinkHref}
                isEditable={isEditable}
                setPlaybookTemplate={setPlaybookTemplate}
            />

            <PlaybookTemplateMainContent
                playbookTemplate={playbookTemplate}
                setPlaybookTemplate={setPlaybookTemplate}
                isEditable={isEditable}
            />
        </div>
    );
};

export default PlaybookTemplateDetailPage;
